import * as Sentry from "@sentry/vue";
import type { CollectionDto } from "@umi/api/schemas/collection";
import type { MintTaskListItem } from "@umi/api/schemas/task";
import type { ChainTag } from "@umi/shared/chains";
import type { MintGroup } from "@umi/shared/types/minting";
import _ from "lodash";
import type { Address, Hex } from "viem";


interface MintEntry {
  address: string,
  collectionId: string,
  hasPending: boolean,
  timestamp: number,
}

type PhaseStats = {
  wanted: number,
  minted: number,
  success: number,
  failed: number,
  canceled: number,
  active: number,
  idle: number,
};

type CollectionStats = {
  total: number,
  idle: number,
  active: number,
  byPhase: Record<string, PhaseStats>,
};

interface State {
  mintTasksById: Record<string, MintTaskListItem>,
  taskIdsByCollection: Record<string, string[]>,
  collectionStats: Record<string, CollectionStats>,
  collectionMap: Record<string, CollectionDto>,
  mintEntries: MintEntry[],
  loadingTasks: boolean,
  cancelingTasks: boolean,
  mintPage: number,
  hasMoreMints: boolean,
  loadingMoreMints: boolean,
  now: number,
  cancellableTickerStarted: boolean,
  taskPagination: Record<string, {
    loading: boolean,
    loaded: boolean,
    loadingMore: boolean,
    hasMore: boolean,
    page: number,
  }>,
}

export type MintPhaseDTO = {
  id: string,
  status: "ignored" | "active" | "idle" | "processed",
  name: string,
  startTime: Date,
  endTime: Date | null,
  wanted: number,
  minted: number,
  progress: {
    success: number,
    failed: number,
    canceled: number,
    wanted: number,
    active: number,
    idle: number,
  },
};

export type MintDTO = {
  status: "idle" | "processed",
  timestamp: number,
  collection: CollectionDto,
  phases: MintPhaseDTO[],
};

export type MintPhaseTaskDto = MintTaskListItem;


const PAGE_LIMIT = 10;
const COLLECTION_TASK_LIMIT = 100;

// Bumped so reload/supersede abandons an in-flight collection drain.
const collectionLoadGeneration: Record<string, number> = {};


const emptyPhaseStats = (): PhaseStats => ({
  wanted: 0,
  minted: 0,
  success: 0,
  failed: 0,
  canceled: 0,
  active: 0,
  idle: 0,
});

const emptyCollectionStats = (): CollectionStats => ({
  total: 0,
  idle: 0,
  active: 0,
  byPhase: {},
});

const countMints = (groups: MintGroup[]) =>
  groups.reduce((sum, group) => sum + group.mints.length, 0);

const recomputeCollectionStats = (state: State, collectionId: string) => {
  const ids = state.taskIdsByCollection[collectionId] ?? [];
  const byPhase: Record<string, PhaseStats> = {};
  let total = 0;
  let idle = 0;
  let active = 0;

  for (const id of ids) {
    const task = state.mintTasksById[id];
    if (!task) continue;

    const phase = byPhase[task.phaseId] ??= emptyPhaseStats();
    phase.wanted += task.quantity;
    phase.minted += task.mintedTokens.length;
    total += task.quantity;

    if (task.status === "success") phase.success += task.quantity;
    else if (task.status === "failed") phase.failed += task.quantity;
    else if (task.status === "canceled") phase.canceled += task.quantity;
    else if (task.status === "active") {
      phase.active += task.quantity;
      active += task.quantity;
    }
    else if (task.status === "idle") {
      phase.idle += task.quantity;
      idle += task.quantity;
    }
  }

  state.collectionStats[collectionId] = { total, idle, active, byPhase };
};


export const useTaskStore = defineStore("task", {

  state: (): State => ({
    mintTasksById: {},
    taskIdsByCollection: {},
    collectionStats: {},
    collectionMap: {},
    mintEntries: [],
    loadingTasks: false,
    cancelingTasks: false,
    mintPage: 0,
    hasMoreMints: false,
    loadingMoreMints: false,
    now: Date.now(),
    cancellableTickerStarted: false,
    taskPagination: {},
  }),


  getters: {

    /**
     * A task is cancellable while idle and before its no-turning-back
     * gate. Past cancellableUntil the engine has locked the send, so
     * the cancel button hides even if the WS update was missed.
     */
    isCancellable(state) {
      return (task: MintTaskListItem) =>
        task.status === "idle"
        && (!task.cancellableUntil || state.now < new Date(task.cancellableUntil).getTime());
    },

    mints() {
      return (chainTag?: ChainTag | null): MintDTO[] => {

        const { collectionMap, mintEntries, collectionStats } = this;

        return mintEntries
          .map(entry => ({ entry, collection: collectionMap[entry.address] }))
          .filter((row): row is { entry: MintEntry, collection: CollectionDto } => !!row.collection)
          .filter(({ collection }) => !chainTag || collection.chainTag === chainTag)
          .map(({ entry, collection }) => {

            const stats = collectionStats[collection.id];

            const phases = collection.phases.map(phase => {

              const phaseStats = stats?.byPhase[phase.id];
              const wantedCount = phaseStats?.wanted ?? 0;
              const mintedCount = phaseStats?.minted ?? 0;
              const activeCount = phaseStats?.active ?? 0;
              const idleCount   = phaseStats?.idle ?? 0;
              const successCount = phaseStats?.success ?? 0;
              const failedCount = phaseStats?.failed ?? 0;
              const canceledCount = phaseStats?.canceled ?? 0;

              const status: MintPhaseDTO["status"] = wantedCount === 0 ? "ignored" :
                activeCount > 0 ? "active" :
                  idleCount ? "idle" : "processed";

              return {
                id: phase.id,
                status,
                name: phase.name,
                startTime: new Date(phase.startTime),
                endTime: phase.endTime ? new Date(phase.endTime) : null,
                wanted: wantedCount,
                minted: mintedCount,
                progress: {
                  success: successCount,
                  failed: failedCount,
                  canceled: canceledCount,
                  wanted: wantedCount,
                  active: activeCount,
                  idle: idleCount,
                },
              };

            });

            const status = stats
              ? (stats.idle + stats.active > 0 ? "idle" as const : "processed" as const)
              : (entry.hasPending ? "idle" as const : "processed" as const);

            return {
              status,
              timestamp: entry.timestamp,
              collection,
              phases,
            };

          });
      };
    },

    /**
     * Return all the tasks for a collection.
     * @returns
     */
    collectionTasks() {
      return (collectionId: string): MintTaskListItem[] => {
        const ids = this.taskIdsByCollection[collectionId] ?? [];
        return ids.flatMap(id => {
          const task = this.mintTasksById[id];
          return task ? [task] : [];
        });
      };
    },

  },

  actions: {

    // Ticks so isCancellable re-evaluates as gates pass
    startCancellableTicker() {
      if (this.cancellableTickerStarted) return;
      this.cancellableTickerStarted = true;
      setInterval(() => { this.now = Date.now(); }, 1000);
    },

    // Wired around the cancel RPC (v3 left the equivalent flag permanently false).
    async cancelTasks<T extends "wallet" | "task" | "mint">(
      collectionId: string,
      type: "wallet" | "task" | "mint",
      ids: T extends "wallet" ? Address[] : T extends "task" ? string[] : never = [] as never
    ) {

      this.cancelingTasks = true;

      const api = useSafeORPC();
      const calls = type === "mint"
        ? [api.tasks.mint.cancel({ type: "mint", collectionId })]
        : type === "task"
          ? (ids as string[]).map(taskId => api.tasks.mint.cancel({ type: "task", taskId }))
          : (ids as Address[]).map(walletAddress =>
            api.tasks.mint.cancel({ type: "wallet", walletAddress })
          );

      const results = await Promise.all(calls);
      this.cancelingTasks = false;

      const otherError = results.find(([error]) => error)?.[0];
      if (otherError) {
        console.error(otherError);
        Sentry.captureException(otherError, { extra: { collectionId, ids, type } });
        showErrorNotification("Failed to cancel mint. Open a support ticket.");
        return;
      }

      const canceledIds = results.flatMap(([, data]) => data?.canceled ?? []);
      const notCancellableIds = results.flatMap(([, data]) => data?.notCancellable ?? []);

      if (canceledIds.length > 0) {
        const canceled = new Set(canceledIds);
        const affected = new Set<string>([collectionId]);
        for (const id of canceled) {
          const task = this.mintTasksById[id];
          if (!task) continue;
          task.status = "canceled";
          affected.add(task.collectionId);
        }
        for (const id of affected) {
          recomputeCollectionStats(this, id);
        }

        const plural  = ids.length > 1 ? "s" : "";
        const message = type === "task" ?
          `${canceled.size} transaction${plural} cancelled successfully` :
          type === "mint" ? "Mint cancelled successfully" :
            `${ids.length} wallet${plural} cancelled successfully`;

        showInfoNotification(message);
      }

      if (notCancellableIds.length > 0) {
        showInfoNotification("These tasks can no longer be canceled.");
      }

    },


    async loadTasks({ chainTag }: { chainTag?: ChainTag } = {}) {

      this.startCancellableTicker();
      this.loadingTasks = true;
      this.mintPage = 1;
      this.mintTasksById = {};
      this.taskIdsByCollection = {};
      this.collectionStats = {};
      this.taskPagination = {};
      this.mintEntries = [];

      const [error, result, , isSuccess] = await useSafeORPC().tasks.mint.listCollections({
        chainTag,
        page: 1,
        limit: PAGE_LIMIT,
      });

      this.loadingTasks = false;

      if (error || !isSuccess || !result) {
        if (isUnauthorizedError(error)) return;
        console.error(error);
        Sentry.captureException(error);
        showErrorNotification("Failed to load mints. Open a support ticket.");
        return;
      }

      for (const collection of _.values(result.collections)) {
        this.collectionMap[collection.address] = collection;
      }

      this.mintEntries = result.entries.map(e => ({
        address: e.address,
        collectionId: e.collectionId,
        hasPending: e.hasPending,
        timestamp: new Date(e.newestTask).getTime(),
      }));

      this.hasMoreMints = result.hasMore;
    },


    async loadMoreMints(chainTag?: ChainTag) {
      if (!this.hasMoreMints || this.loadingMoreMints || this.loadingTasks) return;

      this.loadingMoreMints = true;
      const nextPage = this.mintPage + 1;

      const [error, result, , isSuccess] = await useSafeORPC().tasks.mint.listCollections({
        chainTag,
        page: nextPage,
        limit: PAGE_LIMIT,
      });

      this.loadingMoreMints = false;

      if (error || !isSuccess || !result) {
        if (isUnauthorizedError(error)) return;
        console.error(error);
        Sentry.captureException(error);
        return;
      }

      for (const collection of _.values(result.collections)) {
        this.collectionMap[collection.address] = collection;
      }

      this.mintEntries.push(...result.entries.map(e => ({
        address: e.address,
        collectionId: e.collectionId,
        hasPending: e.hasPending,
        timestamp: new Date(e.newestTask).getTime(),
      })));

      this.mintPage = nextPage;
      this.hasMoreMints = result.hasMore;
    },


    async loadCollectionTasks(collectionId: string) {

      const pagination = this.taskPagination[collectionId];
      if (pagination?.loading) return;

      this.startCancellableTicker();

      collectionLoadGeneration[collectionId] = (collectionLoadGeneration[collectionId] ?? 0) + 1;
      const generation = collectionLoadGeneration[collectionId];

      this.taskPagination[collectionId] = {
        loading: true,
        loaded: false,
        loadingMore: false,
        hasMore: false,
        page: 0,
      };

      const [error, result, , isSuccess] = await useSafeORPC().tasks.mint.list({
        collectionId,
        page: 1,
        limit: COLLECTION_TASK_LIMIT,
      });

      if (generation !== collectionLoadGeneration[collectionId]) return;

      if (error || !isSuccess || !result) {
        console.error(error);
        Sentry.captureException(error, { extra: { collectionId } });
        this.taskPagination[collectionId] = {
          loading: false,
          loaded: false,
          loadingMore: false,
          hasMore: false,
          page: 0,
        };
        return;
      }

      this.addTasksLocally(result.tasks);
      this.taskPagination[collectionId] = {
        loading: false,
        loaded: true,
        loadingMore: false,
        hasMore: result.hasMore,
        page: 1,
      };
    },


    async loadMoreCollectionTasks(collectionId: string) {
      const pagination = this.taskPagination[collectionId];
      if (!pagination?.loaded || !pagination.hasMore || pagination.loading || pagination.loadingMore) return;

      const generation = collectionLoadGeneration[collectionId];
      const page = pagination.page + 1;
      this.taskPagination[collectionId] = { ...pagination, loadingMore: true };

      const [error, result, , isSuccess] = await useSafeORPC().tasks.mint.list({
        collectionId,
        page,
        limit: COLLECTION_TASK_LIMIT,
      });

      if (generation !== collectionLoadGeneration[collectionId]) return;

      if (error || !isSuccess || !result) {
        console.error(error);
        Sentry.captureException(error, { extra: { collectionId, page } });
        const current = this.taskPagination[collectionId];
        if (current) {
          this.taskPagination[collectionId] = { ...current, loadingMore: false };
        }
        return;
      }

      this.addTasksLocally(result.tasks);
      const current = this.taskPagination[collectionId];
      if (current) {
        this.taskPagination[collectionId] = {
          ...current,
          loadingMore: false,
          hasMore: result.hasMore,
          page,
        };
      }
    },


    async reloadCollectionTasks(collectionId: string) {
      collectionLoadGeneration[collectionId] = (collectionLoadGeneration[collectionId] ?? 0) + 1;

      const ids = this.taskIdsByCollection[collectionId] ?? [];
      for (const id of ids) {
        delete this.mintTasksById[id];
      }
      delete this.taskIdsByCollection[collectionId];
      delete this.collectionStats[collectionId];
      delete this.taskPagination[collectionId];

      await this.loadCollectionTasks(collectionId);
    },


    removeCollectionTasksLocally(collectionId: string) {
      const ids = this.taskIdsByCollection[collectionId] ?? [];
      for (const id of ids) {
        delete this.mintTasksById[id];
      }
      delete this.taskIdsByCollection[collectionId];
      this.collectionStats[collectionId] = emptyCollectionStats();

      const pagination = this.taskPagination[collectionId];
      if (pagination) {
        this.taskPagination[collectionId] = { ...pagination, loaded: true };
      } else {
        this.taskPagination[collectionId] = {
          loading: false,
          loaded: true,
          loadingMore: false,
          hasMore: false,
          page: 0,
        };
      }
    },


    addCollectionLocally(collection: CollectionDto) {
      this.collectionMap[collection.address] = collection;
      if (!this.mintEntries.some(e => e.address === collection.address)) {
        this.mintEntries.unshift({
          address: collection.address,
          collectionId: collection.id,
          hasPending: true,
          timestamp: Date.now(),
        });
      }
    },


    addTasksLocally(items: MintTaskListItem[]) {
      const affected = new Set<string>();

      for (const item of items) {
        const isNew = !this.mintTasksById[item.id];
        this.mintTasksById[item.id] = item;
        if (isNew) {
          const ids = this.taskIdsByCollection[item.collectionId] ??= [];
          ids.push(item.id);
        }
        affected.add(item.collectionId);
      }

      for (const collectionId of affected) {
        recomputeCollectionStats(this, collectionId);
      }
    },


    updateTasksLocally(items: MintTaskListItem[]) {
      const affected = new Set<string>();

      for (const item of items) {
        const existing = this.mintTasksById[item.id];
        if (existing) Object.assign(existing, item);
        else if (this.taskPagination[item.collectionId]?.loaded) {
          this.mintTasksById[item.id] = item;
          const ids = this.taskIdsByCollection[item.collectionId] ??= [];
          ids.push(item.id);
        }
        else continue;
        affected.add(item.collectionId);
      }

      for (const collectionId of affected) {
        recomputeCollectionStats(this, collectionId);
      }
    },


    async scheduleMint(opts: {
      collection: CollectionDto,
      mintGroups: MintGroup[],
      drainer?: Address,
      tollHash?: Hex,
    }): Promise<{
      ok: boolean,
      scheduled: number,
      tollRequired?: {
        expectedAmount: string,
        recipient: Address,
        chainTag: string,
        slots: number,
      },
      error?: unknown,
    }> {

      const collectionId = opts.collection.id;
      const scheduled = countMints(opts.mintGroups);

      const [error, result, , isSuccess] = await useSafeORPC().tasks.mint.schedule({
        collectionId,
        drainer: opts.drainer,
        mintGroups: opts.mintGroups,
        tollHash: opts.tollHash,
      });

      if (error || !isSuccess || !result) {
        return { ok: false, scheduled: 0, error: error ?? new Error("Failed to schedule mints") };
      }

      // The server validated the schedule and asks for a toll payment first
      if (result.kind === "tollRequired") {
        return { ok: false, scheduled: 0, tollRequired: result };
      }

      this.addCollectionLocally(opts.collection);
      this.addTasksLocally(result.tasks);

      showSuccessNotification(`${scheduled} mints scheduled`);

      return { ok: true, scheduled };
    },

  },

});
